import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import PageLink from '../components/PageLink';
import PublicLayout, { SUPPORT_EMAIL } from '../components/PublicLayout';

interface QA {
  q: string;
  a: React.ReactNode;
}

interface Group {
  title: string;
  icon: string;
  items: QA[];
}

const GROUPS: Group[] = [
  {
    title: 'General',
    icon: 'home',
    items: [
      {
        q: 'What is Rentagade?',
        a: 'Rentagade is a rental marketplace. Renters browse homes and apartments, apply online and request maintenance or legal services, while merchants and landlords list properties and manage inquiries and applications — all in one place.',
      },
      {
        q: 'Is Rentagade free to use?',
        a: 'Browsing listings, saving favorites, setting search alerts and contacting merchants is free for renters. Some actions carry a fee, such as a rental application fee set for a listing or a paid maintenance or legal service. The price is always shown before you pay.',
      },
      {
        q: 'Where is Rentagade available?',
        a: 'Rentagade currently focuses on rentals in the United States. Listing coverage grows as more merchants join the platform.',
      },
    ],
  },
  {
    title: 'For renters',
    icon: 'search',
    items: [
      {
        q: 'How do I apply for a property?',
        a: 'Open the listing and tap Apply. Fill in your personal, employment and rental history, upload the requested documents, review, and pay the application fee. You will get a reference number and can track the status in the app.',
      },
      {
        q: 'Who sees my rental application?',
        a: 'Only the merchant or landlord responsible for the listing you applied to, plus Rentagade staff when needed to support you. Your documents are never shown publicly.',
      },
      {
        q: 'Is the application fee refundable?',
        a: 'Application fees cover processing and screening and are generally non-refundable once the application is submitted. If you were charged in error, contact support and we will look into it.',
      },
      {
        q: 'What are search alerts?',
        a: 'Save a search (location, price, beds, and so on) and turn on its alert. We will notify you when a new listing matches.',
      },
      {
        q: 'How do I request maintenance or a legal service?',
        a: 'Go to Services in the app, choose a category, describe the issue, add photos and pick a preferred date and time slot. You can follow the order status and timeline from your orders list.',
      },
    ],
  },
  {
    title: 'For merchants & landlords',
    icon: 'building-2',
    items: [
      {
        q: 'How do I list a property?',
        a: (
          <>
            Create a merchant account at{' '}
            <Link to="/agent/register" className="text-brand font-semibold">
              Merchant registration
            </Link>
            . Once your account is approved, sign in to the merchant portal and
            use Add Listing to enter the details and photos.
          </>
        ),
      },
      {
        q: 'Why is my merchant account pending?',
        a: 'New merchant accounts are reviewed by our team before they can publish listings. You will be notified as soon as your account is approved.',
      },
      {
        q: 'Do merchants pay to use Rentagade?',
        a: 'Merchants can start with a limited number of free listings. Subscription plans unlock more listings and features such as featured placement. Current plans and prices are shown in the merchant portal under Subscription.',
      },
      {
        q: 'Where do I see inquiries and applications?',
        a: 'In the merchant portal under Inquiries and on each listing’s detail page, where you can review applications and update their status.',
      },
    ],
  },
  {
    title: 'Payments',
    icon: 'credit-card',
    items: [
      {
        q: 'Which payment methods are accepted?',
        a: 'Major credit and debit cards, processed securely by Stripe.',
      },
      {
        q: 'Does Rentagade store my card details?',
        a: 'No. Card details go directly to Stripe and never touch our servers. We only keep a record of the transaction.',
      },
    ],
  },
  {
    title: 'Account & privacy',
    icon: 'shield',
    items: [
      {
        q: 'I forgot my password. What do I do?',
        a: 'Tap “Forgot password” on the sign-in screen, enter the 6-digit code we email you, then choose a new password.',
      },
      {
        q: 'Can I sign in with Google or Apple?',
        a: 'Yes. Use “Continue with Google” or “Continue with Apple” on the sign-in screen.',
      },
      {
        q: 'How do I delete my account?',
        a: (
          <>
            Email{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-brand font-semibold">
              {SUPPORT_EMAIL}
            </a>{' '}
            from the address on your account with the subject “Delete my
            account”. We delete your account and personal data within 30 days,
            except records we are legally required to keep.
          </>
        ),
      },
      {
        q: 'How is my data used?',
        a: (
          <>
            See our{' '}
            <PageLink slug="privacy-policy" className="text-brand font-semibold">
              Privacy Policy
            </PageLink>{' '}
            for the full details. In short: we use it to run the service, we
            share applications only with the relevant merchant, and we never sell
            your data.
          </>
        ),
      },
    ],
  },
];

const Item: React.FC<{ qa: QA }> = ({ qa }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-g100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 py-4 text-left bg-transparent border-0 cursor-pointer"
      >
        <span className="text-[15px] font-semibold text-ink">{qa.q}</span>
        <span
          className="flex-shrink-0 transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        >
          <Icon name="chevron-down" size={18} color="var(--g500)" />
        </span>
      </button>
      {open && (
        <div className="pb-4 pr-8 text-sm text-g600 leading-relaxed animate-fadeIn">
          {qa.a}
        </div>
      )}
    </div>
  );
};

const Faq: React.FC = () => (
  <PublicLayout title="FAQ">
    <div className="max-w-3xl mx-auto px-4 pt-10">
      <div className="text-[12px] font-bold uppercase tracking-wider text-brand mb-2">
        Help centre
      </div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-ink mb-2">
        Frequently asked questions
      </h1>
      <p className="text-g500 mb-8">
        Quick answers about renting, listing and payments on Rentagade.
      </p>

      <div className="flex flex-col gap-5">
        {GROUPS.map((g) => (
          <div key={g.title} className="card px-5 sm:px-6 pt-5 pb-1">
            <div className="flex items-center gap-2.5 mb-1">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: 'var(--brand-bg)' }}
              >
                <Icon name={g.icon} size={16} color="var(--brand)" />
              </div>
              <h2 className="text-base font-bold text-ink m-0">{g.title}</h2>
            </div>
            {g.items.map((qa) => (
              <Item key={qa.q} qa={qa} />
            ))}
          </div>
        ))}
      </div>

      <div className="card p-6 mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="font-bold text-ink">Still need help?</div>
          <div className="text-sm text-g500">Our support team usually replies within one business day.</div>
        </div>
        <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-primary no-underline">
          <Icon name="mail" size={16} /> Contact support
        </a>
      </div>
    </div>
  </PublicLayout>
);

export default Faq;
